import React from 'react'
import i18next from 'i18next'
import {
  UserIcon,
  DevicePhoneMobileIcon,
  PhotoIcon,
  VideoCameraIcon,
} from '@heroicons/react/24/outline'
import { Drawer } from '../Drawer/Drawer.component'
import { TwButton } from '../Button/TwButton'
import { TextInput } from '../../screens/LoginPage/TextInput'
import { AlbumAutocomplete } from '../AlbumAutocomplete'
import { GalleryFilters } from './galleryFilterParams'
import { useFiltersDrawer } from './FiltersDrawer.hook'
import { FilterSelect } from './FilterSelect.component'
import { PersonSearchSelect } from './PersonSearchSelect.component'
import { SelectedChips } from './SelectedChips.component'

export interface FiltersDrawerProps {
  isOpen: boolean
  onClose: () => void
  workspaceId: string
  filters: GalleryFilters
  onApply: (next: GalleryFilters) => void
  /** AlbumScreen fixes the album via the route */
  hideAlbumField?: boolean
}

const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="mb-1 block text-sm font-medium text-gray-700">{children}</span>
)

export const FiltersDrawer: React.FC<FiltersDrawerProps> = ({
  isOpen,
  onClose,
  workspaceId,
  filters,
  onApply,
  hideAlbumField = false,
}) => {
  const drawer = useFiltersDrawer({ isOpen, workspaceId, filters, onApply, onClose })

  const deviceLabel = (device: { name?: string; make: string; model: string }): string =>
    device.name || `${device.make} ${device.model}`.trim()

  const selectedDevice = drawer.draft.deviceId
    ? drawer.devices.find((device) => device.id === drawer.draft.deviceId)
    : undefined

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={i18next.t('filters.title')}
      footer={
        <div className="flex gap-2">
          {drawer.canClear && (
            <TwButton variant="outline" className="flex-1" onClick={drawer.clearAll}>
              {i18next.t('filters.clearAll')}
            </TwButton>
          )}
          <TwButton className="flex-1" onClick={drawer.apply}>
            {i18next.t('filters.apply')}
          </TwButton>
        </div>
      }
    >
      <div className="flex flex-col gap-5 p-4">
        <TextInput
          label={i18next.t('filters.query')}
          value={drawer.draft.query ?? ''}
          onChange={(e) => drawer.setQuery(e.target.value)}
          placeholder={i18next.t('filters.queryPlaceholder')}
          containerClassName=""
        />

        {!hideAlbumField && (
          <div>
            <FieldLabel>{i18next.t('filters.album')}</FieldLabel>
            <AlbumAutocomplete
              workspaceId={workspaceId}
              value={drawer.selectedAlbum}
              onChange={drawer.setAlbum}
            />
          </div>
        )}

        <div>
          <FieldLabel>{i18next.t('filters.persons')}</FieldLabel>
          <PersonSearchSelect
            workspaceId={workspaceId}
            excludeIds={drawer.draft.personIds ?? []}
            onSelect={drawer.addPerson}
          />
          <SelectedChips
            items={drawer.selectedPersons.map((person) => ({
              id: person.id,
              label: person.name,
              avatarUrl: person.cover_url ?? undefined,
            }))}
            onRemove={drawer.removePerson}
          />
        </div>

        <div>
          <FieldLabel>{i18next.t('filters.users')}</FieldLabel>
          <FilterSelect
            options={drawer.members
              .filter((member) => !drawer.draft.ownerIds?.includes(member.id))
              .map((member) => ({
                id: member.id,
                label: `${member.first_name} ${member.last_name}`.trim(),
                dotColor: member.color,
                avatarUrl: member.avatar ?? undefined,
              }))}
            onSelect={(option) => option && drawer.addOwner(option.id)}
            placeholder={i18next.t('filters.usersPlaceholder')}
            icon={<UserIcon className="h-4 w-4 text-gray-400" />}
            loading={drawer.optionsLoading}
            emptyMessage={i18next.t('filters.noResults')}
            resetKey={drawer.draft.ownerIds?.length ?? 0}
          />
          <SelectedChips
            items={drawer.selectedMembers.map((member) => ({
              id: member.id,
              label: `${member.first_name} ${member.last_name}`.trim(),
              color: member.color,
            }))}
            onRemove={drawer.removeOwner}
          />
        </div>

        <div>
          <FieldLabel>{i18next.t('filters.device')}</FieldLabel>
          <FilterSelect
            options={drawer.devices.map((device) => ({
              id: device.id,
              label: deviceLabel(device),
            }))}
            value={
              selectedDevice
                ? { id: selectedDevice.id, label: deviceLabel(selectedDevice) }
                : undefined
            }
            onSelect={(option) => drawer.setDeviceId(option?.id)}
            placeholder={i18next.t('filters.devicePlaceholder')}
            icon={<DevicePhoneMobileIcon className="h-4 w-4 text-gray-400" />}
            loading={drawer.optionsLoading}
            emptyMessage={i18next.t('filters.noResults')}
            clearable
          />
        </div>

        <div>
          <FieldLabel>{i18next.t('filters.type')}</FieldLabel>
          <div className="flex gap-2">
            {([
              { type: 'image' as const, label: i18next.t('filters.typeImage'), Icon: PhotoIcon },
              { type: 'video' as const, label: i18next.t('filters.typeVideo'), Icon: VideoCameraIcon },
            ]).map(({ type, label, Icon }) => (
              <TwButton
                key={type}
                variant={drawer.draft.type === type ? 'solid' : 'outline'}
                icon={<Icon />}
                onClick={() => drawer.toggleType(type)}
                aria-pressed={drawer.draft.type === type}
                className="flex-1"
              >
                {label}
              </TwButton>
            ))}
          </div>
        </div>

        <div className="flex gap-2">
          <div className="flex-1">
            <TextInput
              label={i18next.t('filters.dateFrom')}
              type="date"
              value={drawer.draft.dateFrom ?? ''}
              onChange={(e) => drawer.setDateFrom(e.target.value)}
              max={drawer.draft.dateTo}
              containerClassName=""
            />
          </div>
          <div className="flex-1">
            <TextInput
              label={i18next.t('filters.dateTo')}
              type="date"
              value={drawer.draft.dateTo ?? ''}
              onChange={(e) => drawer.setDateTo(e.target.value)}
              min={drawer.draft.dateFrom}
              containerClassName=""
            />
          </div>
        </div>
      </div>
    </Drawer>
  )
}
